import { PropsWithChildren } from 'react';
import styled from 'styled-components';
import { Box } from '../atoms';
import Icon from '../atoms/icons/Icon';
import { StyledText } from '../atoms/Texts';
import { colors } from '../utils/colors';
import { formatDate } from '../utils/date';
import { Study } from '../utils/typesForCommonLib';
import {
    capitalizeFirstChar,
    getCityFromTimezone,
    SROnly,
} from '../utils/utils';

type Props = PropsWithChildren<{
    content: {
        ariaLabel: string;
        header: string;
        min: string;
        videoCall: string;
    };
    email?: string;
    startDate: string;
    timezone: {
        value: string;
        translation: string;
    };
    endDate: string;
    language: Study['language'];
    slotLength: string | number;
    locationType: Study['locationType'];
    location: Study['location'];
}>;

export const BookingInformation = ({
    startDate,
    endDate,
    email,
    content,
    children,
    language,
    slotLength,
    locationType,
    location,
    timezone,
}: Props) => {
    const { city, gmtOffset } = getCityFromTimezone(timezone.value, language);
    return (
        <SummaryContainer aria-label={content.ariaLabel}>
            <Box direction="column" gap="12">
                <StyledText variant="h4" weight="bold" as="h2">
                    {content.header}
                </StyledText>
                <Box alignItems="center" gap="8" color="gray600">
                    <Icon name="calendar" />
                    <StyledText variant="body" weight="regular">
                        {capitalizeFirstChar(
                            formatDate({
                                date: startDate,
                                formatType: 'fullDateWithWeekdayAndMonth',
                                lang: language,
                            }),
                        )}
                    </StyledText>
                </Box>
                <SROnly>
                    {formatDate({ date: startDate, formatType: 'timeOnly' })} -
                    {formatDate({ date: endDate, formatType: 'timeOnly' })} (
                    {slotLength} {content.min})
                </SROnly>
                <Box color="gray600" aria-hidden alignItems="center" gap="8">
                    <Icon name="clock" />

                    <StyledText variant="body" weight="regular" as="time">
                        {formatDate({
                            date: startDate,
                            formatType: 'timeOnly',
                        })}{' '}
                        -{' '}
                    </StyledText>
                    <StyledText variant="body" weight="regular" as="time">
                        {formatDate({
                            date: endDate,
                            formatType: 'timeOnly',
                        })}{' '}
                        ({slotLength} {content.min})
                    </StyledText>
                </Box>
                <SROnly>
                    {timezone.translation}: {city} ({gmtOffset})
                </SROnly>

                <Box aria-hidden color="gray600" gap="8">
                    <Icon name="globe" />
                    <StyledText variant="body" weight="regular">
                        {city} ({gmtOffset})
                    </StyledText>
                </Box>

                <Box alignItems="center" gap="8" color="gray600">
                    {locationType == 'address' ? (
                        <>
                            <Icon name="location" />
                            <StyledText variant="body" weight="regular">
                                {location}
                            </StyledText>
                        </>
                    ) : (
                        <>
                            <Icon name="video" />
                            <StyledText variant="body" weight="regular">
                                {content.videoCall}
                            </StyledText>
                        </>
                    )}
                </Box>
                {!!email && (
                    <Box alignItems="center" gap="8" color="gray600">
                        <Icon name="email" />
                        <StyledText variant="body" weight="regular">
                            {email}
                        </StyledText>
                    </Box>
                )}
            </Box>
            {children}
        </SummaryContainer>
    );
};

const SummaryContainer = styled.aside`
    border: 1px solid ${colors.gray200};
    border-radius: 10px;
    gap: 0.75rem;
    display: flex;
    flex-direction: column;
    padding: 1.5rem;
    gap: 1.5rem;
`;
